import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { PlusCircle, Edit, Trash2, Search, ToggleLeft, ToggleRight, GripVertical, Building2, MapPin } from 'lucide-react';
import Header from '../../components/Header';
import { useAuth } from '../../context/AuthContext';
import apiService from '../../services/apiService';

const CategoryManagementPage = () => {
  const { t } = useTranslation();
  const { currentTenant, isGlobalAdmin } = useAuth();
  const [categories, setCategories] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [draggedItem, setDraggedItem] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    icon: '',
    parent_id: null,
    order_index: 0,
    companyId: currentTenant || 'chirakChile',
    is_active: true
  });

  useEffect(() => {
    loadCategories();
  }, [currentTenant, showInactive]); // Recargar cuando cambie el tenant o el filtro de inactivas

  // Filtrar categorías cuando cambia el filtro de empresa
  useEffect(() => {
    if (isGlobalAdmin && allCategories.length > 0) {
      if (selectedCompany === 'all') {
        setCategories(allCategories);
      } else {
        setCategories(allCategories.filter(cat => cat.company_id === selectedCompany));
      }
    }
  }, [selectedCompany, isGlobalAdmin]);

  // Función para asignar imagen predeterminada basada en el nombre
  const getDefaultImage = (categoryName) => {
    const name = categoryName.toLowerCase();
    if (name.includes('repuesto') || name.includes('parte')) return 'https://images.unsplash.com/photo-1558618048-fcd25c85cd64?w=400&h=400&fit=crop&crop=center';
    if (name.includes('aceite') || name.includes('lubricante')) return 'https://images.unsplash.com/photo-1620799140408-edc6dcb6d633?w=400&h=400&fit=crop&crop=center';
    if (name.includes('herramienta') || name.includes('tool')) return 'https://images.unsplash.com/photo-1581092160562-40aa08e78837?w=400&h=400&fit=crop&crop=center';
    if (name.includes('filtro') || name.includes('filter')) return 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&h=400&fit=crop&crop=center';
    if (name.includes('llanta') || name.includes('tire') || name.includes('neumatico')) return 'https://images.unsplash.com/photo-1609531270001-bf5b77ba0a7c?w=400&h=400&fit=crop&crop=center';
    if (name.includes('bateria') || name.includes('battery')) return 'https://images.unsplash.com/photo-1602574968171-0c49fa4bb8f6?w=400&h=400&fit=crop&crop=center';
    if (name.includes('freno') || name.includes('brake')) return 'https://images.unsplash.com/photo-1547036967-23d11aacaee0?w=400&h=400&fit=crop&crop=center';
    if (name.includes('accesorio') || name.includes('accessory')) return 'https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?w=400&h=400&fit=crop&crop=center';
    // Imagen genérica para categorías no identificadas
    return 'https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?w=400&h=400&fit=crop&crop=center';
  };

  const loadCategories = async () => {
    try {
      setLoading(true);
      
      if (isGlobalAdmin) {
        // Admin global: cargar categorías de todas las empresas
        const [chileData, peruData] = await Promise.all([
          apiService.getCategories('chirakChile', showInactive),
          apiService.getCategories('chirakPeru', showInactive)
        ]);
        
        const allCats = [
          ...(Array.isArray(chileData) ? chileData : []).map(cat => ({ ...cat, company_id: 'chirakChile' })),
          ...(Array.isArray(peruData) ? peruData : []).map(cat => ({ ...cat, company_id: 'chirakPeru' }))
        ];
        
        const categoriesWithDefaults = allCats.map(category => ({
          ...category,
          icon: category.icon || getDefaultImage(category.name)
        }));
        
        setAllCategories(categoriesWithDefaults);
        setCategories(categoriesWithDefaults);
      } else {
        // Usuario normal: solo categorías de su empresa
        const data = await apiService.getCategories(null, showInactive);
        const categoriesArray = Array.isArray(data) ? data : (data.categories || data.data || []);
        
        const categoriesWithDefaults = categoriesArray.map(category => ({
          ...category,
          icon: category.icon || getDefaultImage(category.name)
        }));
        
        setAllCategories(categoriesWithDefaults);
        setCategories(categoriesWithDefaults);
      }
    } catch (error) {
      console.error('Error loading categories:', error);
      setCategories([]);
      setAllCategories([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      
      // Subir imagen si hay archivo seleccionado
      let iconUrl = formData.icon || '';
      if (imageFile) {
        try {
          const uploadResult = await apiService.uploadFile(imageFile, 'categories');
          iconUrl = uploadResult.url;
        } catch (err) {
          console.error('Error al subir imagen:', err);
          alert(`Error al subir la imagen: ${err.message}. Continuando sin imagen...`);
        }
      }
      
      const dataToSend = { ...formData, icon: iconUrl };
      
      // Asegurar que el tenant correcto esté configurado
      const originalTenant = localStorage.getItem('current_tenant');
      localStorage.setItem('current_tenant', formData.companyId);
      
      console.log('📤 Enviando datos al backend:', dataToSend);
      
      if (editingCategory) {
        console.log('📝 Actualizando categoría:', editingCategory.id);
        await apiService.updateCategory(editingCategory.id, dataToSend);
      } else {
        console.log('➕ Creando nueva categoría');
        await apiService.createCategory(dataToSend);
      }
      
      // Restaurar tenant original
      if (originalTenant) {
        localStorage.setItem('current_tenant', originalTenant);
      }
      
      setShowForm(false);
      setEditingCategory(null);
      setImageFile(null);
      setImagePreview('');
      setFormData({ 
        name: '', 
        description: '', 
        icon: '',
        parent_id: null,
        order_index: 0, 
        companyId: currentTenant || 'chirakChile',
        is_active: true 
      });
      await loadCategories();
    } catch (error) {
      console.error('Error saving category:', error);
      alert('Error al guardar la categoría: ' + (error.message || 'Error desconocido'));
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      description: category.description || '',
      icon: category.icon || '',
      parent_id: category.parent_id || null,
      order_index: category.order_index || 0,
      companyId: category.company_id || 'chirakChile',
      is_active: category.is_active !== false
    });
    setImageFile(null);
    setImagePreview(category.icon || '');
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingCategory(null);
    setImageFile(null);
    setImagePreview('');
    setFormData({ 
      name: '', 
      description: '', 
      icon: '',
      order_index: 0, 
      companyId: currentTenant || 'chirakChile',
      is_active: true 
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const getImageUrl = (icon) => {
    if (!icon) return null;
    if (icon.startsWith('http')) return icon;
    if (icon.startsWith('data:')) return icon;
    return `${apiService.baseURL}${icon}`;
  };

  // Drag and Drop Handlers
  const handleDragStart = (e, index) => {
    setDraggedItem(filteredCategories[index]);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (!draggedItem) return;

    const targetCategory = filteredCategories[index];
    
    // Si estamos arrastrando una subcategoría, solo permitir moverla dentro de su grupo
    if (draggedItem.isChild) {
      // Verificar que el target también sea hijo del mismo padre
      if (!targetCategory.isChild || targetCategory.parent_id !== draggedItem.parent_id) {
        return; // No permitir mover a otro grupo
      }
    } else {
      // Si estamos arrastrando una categoría padre, no permitir soltarla sobre una subcategoría
      if (targetCategory.isChild) {
        return;
      }
    }

    const draggedIndex = filteredCategories.findIndex(c => c.id === draggedItem.id);
    if (draggedIndex === index) return;

    const newCategories = [...filteredCategories];
    const [removed] = newCategories.splice(draggedIndex, 1);
    newCategories.splice(index, 0, removed);
    
    // Actualizar el estado con la nueva jerarquía
    setCategories(newCategories);
  };

  const handleDragEnd = async () => {
    if (!draggedItem) return;
    
    // Si es admin global y está viendo todas las empresas, no permitir reordenar
    if (isGlobalAdmin && selectedCompany === 'all') {
      alert('Para reordenar categorías, primero selecciona una empresa específica');
      setDraggedItem(null);
      await loadCategories();
      return;
    }
    
    try {
      const updates = [];
      
      if (draggedItem.isChild) {
        // Si es subcategoría, solo actualizar subcategorías del mismo padre
        const siblings = filteredCategories.filter(cat => 
          cat.parent_id === draggedItem.parent_id &&
          (selectedCompany === 'all' || cat.company_id === selectedCompany)
        );
        
        siblings.forEach((cat, index) => {
          if (cat.order_index !== index) {
            updates.push({
              id: cat.id,
              order_index: index
            });
          }
        });
      } else {
        // Si es categoría padre, actualizar todas las categorías padre
        const parentCategories = filteredCategories.filter(cat => 
          !cat.parent_id && 
          (selectedCompany === 'all' || cat.company_id === selectedCompany)
        );
        
        parentCategories.forEach((cat, index) => {
          if (cat.order_index !== index) {
            updates.push({
              id: cat.id,
              order_index: index
            });
          }
        });
      }
      
      // Si no hay cambios, no hacer nada
      if (updates.length === 0) {
        console.log('ℹ️ No hay cambios en el orden');
        setDraggedItem(null);
        return;
      }
      
      console.log(`📦 Actualizando ${updates.length} categorías...`);
      
      // Actualizar solo las categorías que cambiaron
      const results = await Promise.allSettled(
        updates.map(update => 
          apiService.updateCategory(update.id, { order_index: update.order_index })
        )
      );
      
      // Verificar resultados
      const failed = results.filter(r => r.status === 'rejected');
      if (failed.length > 0) {
        console.warn(`⚠️ ${failed.length} de ${updates.length} categorías fallaron`);
        throw new Error('Algunas actualizaciones fallaron');
      }
      
      console.log('✅ Orden actualizado correctamente');
      
      // Recargar categorías para sincronizar
      await loadCategories();
      
    } catch (error) {
      console.error('Error al actualizar el orden:', error);
      alert('Error al guardar el nuevo orden');
      await loadCategories();
    }
    
    setDraggedItem(null);
  };

  const handleToggleActive = async (id) => {
    try {
      const category = categories.find(c => c.id === id);
      if (category) {
        const newActiveState = !category.is_active;
        
        // Actualizar en el backend - solo enviar is_active
        await apiService.updateCategory(id, { is_active: newActiveState });
        
        // Actualizar en el estado local
        setCategories(categories.map(cat => 
          cat.id === id ? { ...cat, is_active: newActiveState } : cat
        ));
        
        // También actualizar en allCategories si existe
        if (allCategories.length > 0) {
          setAllCategories(allCategories.map(cat => 
            cat.id === id ? { ...cat, is_active: newActiveState } : cat
          ));
        }
      }
    } catch (error) {
      console.error('Error toggling category status:', error);
      alert('Error al cambiar el estado de la categoría');
    }
  };

  const handleDelete = async (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    if (!category) return;
    
    try {
      const originalTenant = localStorage.getItem('current_tenant');
      localStorage.setItem('current_tenant', category.company_id);
      
      // Intentar eliminar sin reasignación primero
      const result = await apiService.deleteCategory(categoryId);
      
      if (originalTenant) {
        localStorage.setItem('current_tenant', originalTenant);
      }
      
      await loadCategories();
      alert('Categoría eliminada exitosamente');
    } catch (error) {
      console.error('Error deleting category:', error);
      
      // Si el error es por productos asociados, mostrar opciones
      if (error.data?.requiresReassignment) {
        const errorData = error.data;
        const availableCategories = categories.filter(c => 
          c.id !== categoryId && 
          c.company_id === category.company_id && 
          c.is_active
        );
        
        if (availableCategories.length === 0) {
          alert(`No se puede eliminar: ${errorData.message}\n\nNo hay otras categorías disponibles para reasignar los productos.`);
          return;
        }
        
        // Mostrar diálogo para seleccionar categoría destino
        const message = `${errorData.message}\n\nSelecciona una categoría destino para reasignar los productos:`;
        const options = availableCategories.map((c, i) => `${i + 1}. ${c.name}`).join('\n');
        const selection = prompt(`${message}\n\n${options}\n\nIngresa el número de la categoría destino:`);
        
        if (selection) {
          const index = parseInt(selection) - 1;
          if (index >= 0 && index < availableCategories.length) {
            const targetCategory = availableCategories[index];
            
            try {
              const originalTenant = localStorage.getItem('current_tenant');
              localStorage.setItem('current_tenant', category.company_id);
              
              await apiService.deleteCategory(categoryId, targetCategory.id);
              
              if (originalTenant) {
                localStorage.setItem('current_tenant', originalTenant);
              }
              
              await loadCategories();
              alert(`Categoría eliminada y ${errorData.productsCount} productos reasignados a "${targetCategory.name}"`);
            } catch (reassignError) {
              console.error('Error en reasignación:', reassignError);
              alert('Error al reasignar productos: ' + (reassignError.message || 'Error desconocido'));
            }
          } else {
            alert('Selección inválida');
          }
        }
      } else {
        alert('Error al eliminar la categoría: ' + (error.message || 'Error desconocido'));
      }
    }
  };

  // Organizar categorías en jerarquía: padres con sus hijos
  const organizeHierarchy = () => {
    // Filtrar según búsqueda y empresa
    const filtered = categories.filter(category => {
      const matchesSearch = category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            (category.description || '').toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCompany = selectedCompany === 'all' || category.company_id === selectedCompany;
      return matchesSearch && matchesCompany;
    });
    
    // Separar padres e hijos
    const parents = filtered.filter(cat => !cat.parent_id);
    const children = filtered.filter(cat => cat.parent_id);
    
    // Construir jerarquía
    const hierarchy = [];
    parents.forEach(parent => {
      hierarchy.push({ ...parent, isParent: true });
      // Agregar hijos justo después del padre
      const parentChildren = children.filter(child => child.parent_id === parent.id);
      parentChildren.forEach(child => {
        hierarchy.push({ ...child, isChild: true });
      });
    });
    
    return hierarchy;
  };
  
  const filteredCategories = organizeHierarchy();

  // Estadísticas por empresa
  const getStats = () => {
    const chile = categories.filter(cat => cat.company_id === 'chirakChile').length;
    const peru = categories.filter(cat => cat.company_id === 'chirakPeru').length;
    return { chile, peru, total: categories.length };
  };

  const stats = getStats();

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
        <Header />
        <div className="flex justify-center items-center min-h-screen">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          className="mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-4xl font-bold text-gray-900 mb-4">{t('category.title')}</h2>
          <p className="text-xl text-gray-600">Administra las categorías de productos disponibles en el portal.</p>
        </motion.div>

        <motion.div
          className="bg-white/80 backdrop-blur-xl rounded-2xl p-6 mb-8 shadow-lg border border-white/20"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          {/* Estadísticas */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-gradient-to-r from-blue-500 to-purple-600 p-4 rounded-xl text-white">
              <div className="text-2xl font-bold">{stats.total}</div>
              <div className="text-sm opacity-90">{t('category.title')}</div>
            </div>
            <div className="bg-gradient-to-r from-blue-400 to-blue-600 p-4 rounded-xl text-white">
              <div className="text-2xl font-bold">{stats.chile}</div>
              <div className="text-sm opacity-90">Chirak Chile</div>
            </div>
            <div className="bg-gradient-to-r from-red-400 to-red-600 p-4 rounded-xl text-white">
              <div className="text-2xl font-bold">{stats.peru}</div>
              <div className="text-sm opacity-90">Chirak Perú</div>
            </div>
            <div className="bg-gradient-to-r from-green-400 to-green-600 p-4 rounded-xl text-white">
              <div className="text-2xl font-bold">{filteredCategories.length}</div>
              <div className="text-sm opacity-90">{t('common.filter')}</div>
            </div>
          </div>

          <div className="flex flex-col md:flex-row gap-4 items-center justify-between mb-6">
            <div className="flex gap-4 flex-1">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder={t('common.search')}
                  className="w-full pl-12 pr-4 py-3 bg-gray-50/80 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all duration-300"
                />
              </div>
              {isGlobalAdmin && (
                <select
                  value={selectedCompany}
                  onChange={(e) => setSelectedCompany(e.target.value)}
                  className="px-4 py-3 bg-gray-50/80 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all duration-300"
                >
                  <option value="all">Todas las empresas</option>
                  <option value="chirakChile">Chirak Chile</option>
                  <option value="chirakPeru">Chirak Perú</option>
                </select>
              )}
              <label className="flex items-center gap-2 px-4 py-3 bg-gray-50/80 border border-gray-200 rounded-xl cursor-pointer hover:bg-gray-100/80 transition-all duration-300">
                <input
                  type="checkbox"
                  checked={showInactive}
                  onChange={(e) => setShowInactive(e.target.checked)}
                  className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700 whitespace-nowrap">Mostrar inactivas</span>
              </label>
            </div>
            <motion.button
              onClick={() => setShowForm(true)}
              className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-300"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <PlusCircle className="w-5 h-5" />
              {t('category.add')}
            </motion.button>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full bg-white rounded-xl shadow-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200 text-gray-600 uppercase text-sm leading-normal">
                  <th className="py-3 px-6 text-left">{t('category.order')}</th>
                  <th className="py-3 px-6 text-left">{t('category.name')}</th>
                  {isGlobalAdmin && <th className="py-3 px-6 text-left">{t('company.title')}</th>}
                  <th className="py-3 px-6 text-left">{t('common.description')}</th>
                  <th className="py-3 px-6 text-center">{t('common.status')}</th>
                  <th className="py-3 px-6 text-center">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody className="text-gray-700 text-sm font-light">
                <AnimatePresence>
                  {filteredCategories.map((category, index) => (
                    <motion.tr
                      key={category.id}
                      className={`border-b border-gray-200 hover:bg-gray-50 ${draggedItem && draggedItem.id === category.id ? 'opacity-50' : ''} ${category.isChild ? 'bg-purple-50/30' : ''}`}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.3 }}
                      draggable={true}
                      onDragStart={(e) => handleDragStart(e, index)}
                      onDragOver={(e) => handleDragOver(e, index)}
                      onDragEnd={handleDragEnd}
                    >
                      <td className="py-4 px-6 text-center cursor-grab">
                        {category.isChild ? (
                          <div className="w-5 h-5 mx-auto flex items-center justify-center">
                            <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                          </div>
                        ) : (
                          <GripVertical className="w-5 h-5 text-gray-400 mx-auto" />
                        )}
                      </td>
                      <td className="py-4 px-6 text-left whitespace-nowrap">
                        <div className="flex items-center" style={{ paddingLeft: category.isChild ? '2.5rem' : '0' }}>
                          {category.isChild && (
                            <span className="text-purple-400 mr-3 text-lg font-bold">↳</span>
                          )}
                          {category.icon ? (
                            <img 
                              src={getImageUrl(category.icon)} 
                              alt={category.name}
                              className={`w-10 h-10 rounded-lg object-cover mr-3 ${category.isChild ? 'opacity-80 border-2 border-purple-200' : ''}`}
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                          ) : null}
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center mr-3 text-white font-bold text-sm ${category.icon ? 'hidden' : 'flex'} ${
                            category.isChild
                              ? 'bg-gradient-to-r from-purple-400 to-pink-400'
                              : category.company_id === 'chirakChile' 
                                ? 'bg-gradient-to-r from-blue-500 to-blue-600' 
                                : 'bg-gradient-to-r from-red-500 to-red-600'
                          }`}>
                            {category.name.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className={`font-medium ${category.isChild ? 'text-purple-700' : 'text-gray-900'} ${!category.is_active ? 'line-through' : ''}`}>
                                {category.name}
                              </span>
                              {category.isChild && (
                                <span className="px-2 py-0.5 text-xs bg-purple-100 text-purple-700 rounded-full font-semibold border border-purple-200">
                                  📁 Subcategoría
                                </span>
                              )}                              {!category.is_active && (
                                <span className="px-2 py-0.5 text-xs bg-red-100 text-red-700 rounded-full font-semibold border border-red-200">
                                  🚫 Inactiva
                                </span>
                              )}                              {!category.is_active && (
                                <span className="px-2 py-0.5 text-xs bg-red-100 text-red-700 rounded-full font-semibold border border-red-200">
                                  🚫 Inactiva
                                </span>
                              )}
                            </div>
                            <span className="text-xs text-gray-500 block mt-0.5">ID: {category.id.substring(0, 8)}...</span>
                          </div>
                        </div>
                      </td>
                      {isGlobalAdmin && (
                        <td className="py-4 px-6 text-left">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            category.company_id === 'chirakChile' 
                              ? 'bg-blue-100 text-blue-700' 
                              : 'bg-red-100 text-red-700'
                          }`}>
                            {category.company_id === 'chirakChile' ? 'Chile' : 'Perú'}
                          </span>
                        </td>
                      )}
                      <td className="py-4 px-6 text-left">
                        <span className="text-gray-600">
                          {category.description || 'Sin descripción'}
                        </span>
                      </td>
                      <td className="py-4 px-6 text-center">
                        <motion.button
                          onClick={() => handleToggleActive(category.id)}
                          className={`p-2 rounded-full transition-colors ${
                            category.is_active ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'
                          }`}
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                        >
                          {category.is_active ? <ToggleRight className="w-6 h-6" /> : <ToggleLeft className="w-6 h-6" />}
                        </motion.button>
                      </td>
                      <td className="py-4 px-6 text-center">
                        <div className="flex item-center justify-center gap-2">
                          <motion.button
                            onClick={() => handleEdit(category)}
                            className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center hover:bg-blue-200 transition-colors"
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                          >
                            <Edit className="w-4 h-4" />
                          </motion.button>
                          <motion.button
                            onClick={() => handleDelete(category.id)}
                            className="w-8 h-8 rounded-full bg-red-100 text-red-600 flex items-center justify-center hover:bg-red-200 transition-colors"
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                          >
                            <Trash2 className="w-4 h-4" />
                          </motion.button>
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </AnimatePresence>
              </tbody>
            </table>
          </div>

          {filteredCategories.length === 0 && (
            <motion.div
              className="text-center py-12 text-gray-500"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              {t('messages.noResults')}
            </motion.div>
          )}
        </motion.div>
      </div>

      <AnimatePresence>
        {showForm && (
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-white rounded-3xl p-8 shadow-2xl max-w-lg w-full border border-white/20"
              initial={{ y: -50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 50, opacity: 0 }}
              transition={{ type: "spring", damping: 20, stiffness: 300 }}
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                {editingCategory ? t('category.edit') : t('category.add')}
              </h3>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('category.name')} *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full px-4 py-3 bg-gray-50/80 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all duration-300"
                    required
                    placeholder="Nombre de la categoría"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('company.title')} *
                  </label>
                  <select
                    value={formData.companyId}
                    onChange={(e) => setFormData({...formData, companyId: e.target.value})}
                    className="w-full px-4 py-3 bg-gray-50/80 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all duration-300"
                  >
                    <option value="chirakChile">🇨🇱 Chirak Chile</option>
                    <option value="chirakPeru">🇵🇪 Chirak Perú</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('category.parent')}
                  </label>
                  <select
                    value={formData.parent_id || ''}
                    onChange={(e) => setFormData({...formData, parent_id: e.target.value || null})}
                    className="w-full px-4 py-3 bg-gray-50/80 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all duration-300"
                  >
                    <option value="">Sin categoría padre (Categoría principal)</option>
                    {categories
                      .filter(cat => !cat.parent_id && cat.id !== editingCategory?.id && cat.company_id === formData.companyId)
                      .map(cat => (
                        <option key={cat.id} value={cat.id}>
                          {cat.name}
                        </option>
                      ))
                    }
                  </select>
                  <p className="mt-1 text-xs text-gray-500">
                    Si seleccionas una categoría padre, esta será una subcategoría
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('category.icon')}
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="w-full px-4 py-3 bg-gray-50/80 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all duration-300 text-sm"
                  />
                  {imagePreview && (
                    <div className="mt-3 flex justify-center">
                      <img 
                        src={getImageUrl(imagePreview)} 
                        alt="Vista previa" 
                        className="w-24 h-24 rounded-lg object-cover border-2 border-blue-200 shadow-sm"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('common.description')}
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    className="w-full px-4 py-3 bg-gray-50/80 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all duration-300"
                    rows="3"
                    placeholder="Descripción opcional de la categoría"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('category.order')}
                  </label>
                  <input
                    type="number"
                    value={formData.order_index}
                    onChange={(e) => setFormData({...formData, order_index: parseInt(e.target.value) || 0})}
                    className="w-full px-4 py-3 bg-gray-50/80 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all duration-300"
                    placeholder="0"
                  />
                </div>
                
                <div className="flex justify-end gap-3 pt-6">
                  <motion.button
                    type="button"
                    onClick={handleCancel}
                    className="px-6 py-3 bg-gray-200 text-gray-800 rounded-xl font-semibold hover:bg-gray-300 transition-all duration-300"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {t('common.cancel')}
                  </motion.button>
                  <motion.button
                    type="submit"
                    className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-300"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {editingCategory ? t('common.save') : t('common.add')}
                  </motion.button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CategoryManagementPage;